import { defineStore } from 'pinia';
import {
  CalendarEvent,
  calendarFailure,
  contextChanged,
  createRequestId,
  normalizeCalendarReply,
} from '~utils/calendarProtocol';
import {
  addDays,
  findBranchRule,
  getAvailability,
  getBranchDay,
  getPatternDates,
  isValidDate,
  toMinutes,
  todayIso,
  toTime,
  weekdayOf,
  weekStart,
  WEEKDAY_NAMES,
} from '~utils/calendarModel';
import type {
  Availability,
  Branch,
  BranchDay,
  BranchFeedback,
  BranchRule,
  CalendarCommand,
  CalendarContext,
  CalendarEventHandler,
  CalendarMode,
  CalendarRequest,
  CalendarResult,
  CalendarScreen,
  CalendarSelection,
  DayHours,
  Doctor,
  Feedback,
  HoursType,
  PatternSpec,
  Room,
  ScheduleState,
  SchedulePlan,
  Slot,
  CalendarBreak,
  Snapshot,
} from './types';

type Connection = {
  message: string;
  retry: boolean;
};

export type DraftDayKind = 'week' | 'exception';

type Transport = {
  request: CalendarRequest;
  onEvent?: CalendarEventHandler;
};

const MAX_ID = 2147483647;
// Снимок грузится с запасом в обе стороны, чтобы листание недель не дёргало сервер
const SNAPSHOT_RADIUS_DAYS = 42;
const NARROW_WIDTH = 680;
const CONDENSED_WIDTH = 940;
const UNCERTAIN_CODES = ['TIMEOUT', 'REQUEST_TIMEOUT', 'REQUEST_FAILED', 'INVALID_RESPONSE'];

// Поколение контекста живёт вне state: $reset не должен обнулять счётчик,
// иначе ответ старого запроса примется за актуальный
let generation = 0;
let transport: Transport | null = null;

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));

const emptySchedule = (): ScheduleState => ({
  revision: 0,
  slots: [],
  appointments: [],
  breaks: [],
  branchRules: [],
  logs: [],
});

const isValidId = (id: unknown): boolean => (
  id == null || (Number.isSafeInteger(id) && (id as number) > 0 && (id as number) <= MAX_ID)
);

const normalizeContext = (value: CalendarContext): CalendarContext | null => {
  const { id_staff, id_branch, id_patient } = value;
  if (![id_staff, id_branch, id_patient].every(isValidId)) return null;
  const hasDate = value.date != null && value.date !== '';
  if (hasDate && !isValidDate(value.date)) return null;
  return Object.freeze({
    id_staff: id_staff ?? null,
    id_branch: id_branch ?? null,
    id_patient: id_patient ?? null,
    ...(hasDate ? { date: value.date } : {}),
  });
};

const isSnapshot = (data: unknown): data is Snapshot => {
  const snapshot = data as Snapshot | undefined;
  return !!snapshot?.state
    && Array.isArray(snapshot.state.slots)
    && Array.isArray(snapshot.state.appointments)
    && Array.isArray(snapshot.doctors)
    && Array.isArray(snapshot.rooms)
    && Array.isArray(snapshot.branches)
    && Number.isSafeInteger(snapshot.actorStaffId);
};

const isRoomAllowed = (room: Room, staffId: number | null): boolean => (
  !Array.isArray(room.allowedStaffIds) || room.allowedStaffIds.includes(Number(staffId))
);

const emptyWeek = () => WEEKDAY_NAMES.map((_, index) => ({
  weekday: index + 1,
  open: [],
  intake: [],
}));

const commandEventName = (command: CalendarCommand): string => {
  if (command[0] === 'book') return 'calendar.booked';
  if (command[0] === 'cancel') return 'calendar.cancelled';
  return 'calendar.changed';
};

export const useScheduleStore = defineStore('schedule', {
  state: () => ({
    connection: { message: 'Подключение к медицинской системе…', retry: false } as Connection | null,
    context: null as CalendarContext | null,
    date: todayIso(),
    staffId: null as number | null,
    branchId: null as number | null,
    actorStaffId: null as number | null,
    patientId: null as number | null,

    doctors: [] as Doctor[],
    rooms: [] as Room[],
    branches: [] as Branch[],
    editableStaffIds: [] as number[],
    manageableBranchIds: [] as number[],
    schedule: emptySchedule(),

    screen: 'calendar' as CalendarScreen,
    mode: 'mine' as CalendarMode,
    layoutWidth: 0,

    snapshotSequence: 0,
    loading: false,
    dataFresh: false,
    mutating: false,
    initialized: false,

    toast: null as Feedback | null,
    selection: null as CalendarSelection | null,
    roomMenuSlotId: null as number | null,

    builderOpen: false,
    plan: {
      from: todayIso(),
      to: todayIso(),
      pattern: 'once',
      anchor: todayIso(),
      start: '09:00',
      end: '16:00',
      duration: 30,
      roomId: null,
      breaks: [],
    } as SchedulePlan,
    planPreview: null as Feedback | null,

    branchDraft: null as BranchRule | null,
    branchDirty: false,
    branchBaseRevision: 0,
    branchFeedback: null as BranchFeedback | null,
  }),

  getters: {
    busy: (state): boolean => (
      !state.initialized || !state.dataFresh || state.loading || state.mutating
    ),
    narrow: (state): boolean => state.layoutWidth < NARROW_WIDTH,
    condensed: (state): boolean => (
      state.layoutWidth >= NARROW_WIDTH && state.layoutWidth < CONDENSED_WIDTH
    ),

    doctorById: (state) => (id: number | null): Doctor | undefined => (
      state.doctors.find((doctor) => doctor.id === Number(id))
    ),
    // Фамилия для коротких подписей: «Иванов», «Петрова»
    lastName(): (id: number | null) => string {
      return (id) => this.doctorById(id)?.name.split(' ').pop() || 'Пользователь';
    },
    roomNumber: (state) => (id: number): string | number => {
      const room = state.rooms.find((item) => item.id === Number(id));
      return room?.number ?? id;
    },

    staffDoctor(): Doctor | undefined {
      return this.doctorById(this.staffId);
    },
    actorDoctor(): Doctor | undefined {
      return this.doctorById(this.actorStaffId);
    },
    currentBranch: (state): Branch | undefined => (
      state.branches.find((branch) => branch.id === state.branchId)
    ),
    // Сервер отдаёт врачей по id — для выбора в «Команде» сортируем по ФИО
    editableDoctors: (state): Doctor[] => state.doctors
      .filter((doctor) => state.editableStaffIds.includes(doctor.id))
      .sort((a, b) => a.name.localeCompare(b.name, 'ru')),
    allowedRooms: (state): Room[] => state.rooms.filter(
      (room) => room.branchId === state.branchId && isRoomAllowed(room, state.staffId),
    ),
    defaultRoomId(): number | null {
      return this.allowedRooms[0]?.id || null;
    },
    canEdit: (state): boolean => state.editableStaffIds.includes(Number(state.staffId)),
    canManage: (state): boolean => state.manageableBranchIds.includes(Number(state.branchId)),
    // Заполнять график можно только в своём/подчинённом календаре и при наличии кабинетов
    canPlan(): boolean {
      return this.canEdit && this.allowedRooms.length > 0;
    },

    // День черновика режима: строка недели или особая дата
    draftDay: (state) => (kind: DraftDayKind, index: number): DayHours | undefined => (
      kind === 'week' ? state.branchDraft?.week[index] : state.branchDraft?.exceptions[index]
    ),
    branchDay: (state) => (date: string): BranchDay => (
      getBranchDay(state.schedule.branchRules, state.branchId, date)
    ),
    weekDays: (state): string[] => {
      const monday = weekStart(state.date);
      return Array.from({ length: 7 }, (_, index) => addDays(monday, index));
    },
    // На узком экране показывается только выбранный день
    displayDays(): string[] {
      return this.narrow ? [this.date] : this.weekDays;
    },
    // Границы сетки — от самого раннего открытия до самого позднего закрытия филиала
    viewHours(): { from: string; to: string } {
      const opening = this.displayDays.flatMap((day) => this.branchDay(day).open);
      if (!opening.length) return { from: '', to: '' };
      return {
        from: toTime(Math.min(...opening.map((item) => toMinutes(item.start)))),
        to: toTime(Math.max(...opening.map((item) => toMinutes(item.end)))),
      };
    },

    // Занятость кабинета roomId на время приёма slot (сам приём не учитывается)
    slotAvailability: (state) => (slot: Slot, roomId = slot.roomId): Availability => (
      getAvailability(state.schedule, state.rooms, { ...slot, roomId, excludeSlotId: slot.id })
    ),

    selectedSlot(): Slot | null {
      const { selection } = this;
      if (selection?.kind !== 'slot') return null;
      return this.schedule.slots.find(
        (slot) => slot.id === selection.id && slot.staffId === this.staffId,
      ) || null;
    },
    selectedBreak(): CalendarBreak | null {
      const { selection } = this;
      if (selection?.kind !== 'break') return null;
      return this.schedule.breaks.find((item) => item.id === selection.id) || null;
    },

    patternSpec(): PatternSpec {
      const { plan } = this;
      return {
        staffId: this.staffId,
        roomId: Number(plan.roomId),
        // «Выбранный день» — это период из одной даты
        dates: getPatternDates({
          from: plan.from,
          to: plan.pattern === 'once' ? plan.from : plan.to,
          pattern: plan.pattern === 'once' ? 'every' : plan.pattern,
          anchor: plan.anchor,
        }),
        start: plan.start,
        end: plan.end,
        duration: Number(plan.duration),
        breaks: clone(plan.breaks),
      };
    },
  },

  actions: {
    connect(request: CalendarRequest, onEvent?: CalendarEventHandler) {
      transport = { request, onEvent };
    },

    disconnect() {
      generation += 1;
      transport = null;
      this.$reset();
    },

    tell(text: string, error = false) {
      this.toast = { ok: !error, text };
    },

    showConnection(message: string, retry = false) {
      this.connection = { message, retry };
    },

    async request(event: CalendarEvent, payload: Record<string, unknown>): Promise<CalendarResult> {
      const requestGeneration = generation;
      if (!transport || !this.context) return contextChanged();
      try {
        const result = await transport.request(event, payload, createRequestId());
        if (requestGeneration !== generation) return contextChanged();
        return normalizeCalendarReply(result) as CalendarResult;
      } catch {
        if (requestGeneration !== generation) return contextChanged();
        return calendarFailure(
          'REQUEST_FAILED',
          'Не удалось выполнить запрос календаря. Проверьте подключение и повторите загрузку.',
        );
      }
    },

    async setContext(value: CalendarContext = {}): Promise<boolean> {
      generation += 1;
      const context = normalizeContext(value);
      this.context = context;
      if (!context) {
        this.showConnection(
          'Некорректный контекст календаря: проверьте врача, филиал, пациента и дату.',
        );
        return false;
      }

      const staffId = context.id_staff ?? this.staffId;
      const branchId = context.id_branch ?? this.branchId;
      const changed = staffId !== this.staffId || branchId !== this.branchId;

      this.snapshotSequence += 1;
      this.loading = false;
      this.dataFresh = false;
      this.mutating = false;
      this.staffId = staffId;
      this.branchId = branchId;
      this.patientId = context.id_patient ?? null;
      if (context.date) this.date = context.date;
      this.selection = null;
      this.builderOpen = false;
      this.roomMenuSlotId = null;
      this.toast = null;
      if (changed) {
        this.initialized = false;
        this.branchDraft = null;
        this.branchDirty = false;
        this.plan = this.makePlan();
        this.screen = 'calendar';
      }

      this.showConnection('Загрузка расписания…');
      const requestGeneration = generation;
      try {
        return await this.loadSnapshot();
      } catch {
        if (requestGeneration === generation) {
          this.showConnection('Не удалось загрузить календарь. Повторите подключение.', true);
        }
        return false;
      }
    },

    retry() {
      this.showConnection('Подключение к медицинской системе…');
      return this.setContext(this.context || {});
    },

    refresh(): Promise<boolean> {
      return this.mutating ? Promise.resolve(false) : this.loadSnapshot();
    },

    async loadSnapshot(): Promise<boolean> {
      this.snapshotSequence += 1;
      const sequence = this.snapshotSequence;
      const requestGeneration = generation;
      const requestedBranch = this.branchId;
      this.loading = true;
      this.dataFresh = false;

      const result = await this.request('snapshot', {
        from: addDays(this.date, -SNAPSHOT_RADIUS_DAYS),
        to: addDays(this.date, SNAPSHOT_RADIUS_DAYS),
        ...(this.branchId ? { branchId: this.branchId } : {}),
      });
      if (sequence !== this.snapshotSequence || requestGeneration !== generation) return false;
      this.loading = false;

      if (!result.ok) {
        if (this.initialized) this.tell(result.message, true);
        else this.showConnection(result.message, true);
        return false;
      }
      const { data } = result;
      if (!isSnapshot(data)) {
        this.showConnection('Сервер вернул неполные данные календаря.', true);
        return false;
      }

      this.schedule = {
        ...data.state,
        breaks: data.state.breaks || [],
        branchRules: data.state.branchRules || [],
        logs: data.state.logs || [],
      };
      this.doctors = data.doctors;
      this.rooms = data.rooms;
      this.branches = data.branches;
      this.actorStaffId = data.actorStaffId;
      this.editableStaffIds = data.editableStaffIds || [];
      this.manageableBranchIds = data.manageableBranchIds || [];

      if (!this.actorDoctor) {
        this.showConnection('Сервер не вернул врача текущего пользователя.', true);
        return false;
      }
      if (this.staffId == null) this.staffId = this.actorStaffId;
      if (this.branchId == null && !this.resolveBranch(data)) {
        this.showConnection('Сервер не вернул доступный филиал календаря.', true);
        return false;
      }
      if (!this.staffDoctor || !this.currentBranch) {
        this.showConnection('Выбранный врач или филиал недоступен.', true);
        return false;
      }
      // Филиал выбран на клиенте — перезапрашиваем снимок уже по нему
      if (requestedBranch == null && data.branchId === undefined) return this.loadSnapshot();

      this.mode = this.staffId === this.actorStaffId ? 'mine' : 'team';
      if (!this.initialized) this.plan = this.makePlan();
      this.ensurePlanRoom();
      if (!this.branchDraft || !this.branchDirty) {
        this.branchDraft = this.makeBranchDraft(this.branchDraft?.effectiveFrom || this.date);
        this.branchBaseRevision = this.schedule.revision;
      }
      if (!this.canManage) this.screen = 'calendar';
      this.dropStaleSelection();

      this.dataFresh = true;
      this.initialized = true;
      this.connection = null;
      return true;
    },

    // Филиал по умолчанию: от сервера, иначе — первый, где у врача есть кабинет
    resolveBranch(data: Snapshot): boolean {
      if (data.branchId === undefined) {
        const room = this.rooms.find((item) => (
          this.branches.some((branch) => branch.id === item.branchId)
          && isRoomAllowed(item, this.staffId)
        ));
        this.branchId = room?.branchId || this.branches[0]?.id || null;
        return true;
      }
      const known = Number.isSafeInteger(data.branchId)
        && this.branches.some((branch) => branch.id === data.branchId);
      if (known) this.branchId = data.branchId as number;
      return known;
    },

    async runCommand(command: CalendarCommand, message = ''): Promise<CalendarResult> {
      if (this.mutating || this.loading || !this.dataFresh) {
        return calendarFailure('BUSY', 'Дождитесь обновления календаря.');
      }
      const requestGeneration = generation;
      this.mutating = true;
      try {
        const result = await this.request('command', {
          command,
          expectedRevision: this.schedule.revision,
        });
        if (requestGeneration !== generation) return contextChanged();

        if (!result.ok) {
          const uncertain = UNCERTAIN_CODES.includes(String(result.code));
          if (result.code !== 'REVISION_CONFLICT' && !uncertain) {
            this.tell(result.message, true);
            return result;
          }
          // Конфликт версий или неизвестный исход — перечитываем календарь
          this.dataFresh = false;
          const refreshed = await this.loadSnapshot();
          if (requestGeneration !== generation) return contextChanged();
          if (!uncertain) this.tell(result.message, true);
          else if (refreshed) {
            this.tell('Сервер не подтвердил результат операции. Календарь обновлён; проверьте изменения перед повтором.', true);
          } else this.tell('Результат операции неизвестен. Обновите календарь перед повтором.', true);
          return result;
        }

        if (command[0] === 'pattern' && command[1].dates[0]) [this.date] = command[1].dates;
        this.roomMenuSlotId = null;
        const refreshed = await this.loadSnapshot();
        if (requestGeneration !== generation) return contextChanged();
        if (refreshed) this.tell(message || result.message || 'Изменения сохранены.');
        else {
          this.tell('Изменения сохранены. Не удалось загрузить актуальный календарь; нажмите «Обновить».', true);
        }
        transport?.onEvent?.(commandEventName(command), {
          operation: command[0],
          revision: result.revision,
        });
        return result;
      } finally {
        if (requestGeneration === generation) this.mutating = false;
      }
    },

    preview(command: CalendarCommand): Promise<CalendarResult> {
      return this.request('preview', { command, expectedRevision: this.schedule.revision });
    },

    // --- Навигация ---

    pickDay(date: string) {
      this.date = date;
      this.selection = null;
      this.roomMenuSlotId = null;
    },

    async setDate(date: string) {
      if (!isValidDate(date)) return;
      this.pickDay(date);
      await this.loadSnapshot();
    },

    async shiftWeek(direction: 1 | -1) {
      this.pickDay(addDays(this.date, direction * 7));
      await this.loadSnapshot();
    },

    setScreen(screen: CalendarScreen) {
      if (screen === 'branch' && !this.canManage) return;
      this.screen = screen;
      this.roomMenuSlotId = null;
    },

    setMode(mode: CalendarMode) {
      this.mode = mode;
      if (mode === 'mine') this.staffId = this.actorStaffId;
      this.selection = null;
      this.roomMenuSlotId = null;
      this.ensurePlanRoom();
    },

    setStaff(staffId: number) {
      if (!this.editableStaffIds.includes(staffId)) return;
      this.staffId = staffId;
      this.selection = null;
      this.roomMenuSlotId = null;
      this.ensurePlanRoom();
    },

    // --- Выделение в сетке ---

    selectSlot(id: number) {
      const slot = this.schedule.slots.find((item) => item.id === id);
      if (!slot) return;
      this.selection = {
        kind: 'slot', id, date: slot.date, start: slot.start, end: slot.end,
      };
      this.builderOpen = false;
      this.roomMenuSlotId = null;
    },

    selectBreak(id: number) {
      const item = this.schedule.breaks.find((rest) => rest.id === id);
      if (!item) return;
      this.selection = {
        kind: 'break', id, date: item.date, start: item.start, end: item.end,
      };
      this.builderOpen = false;
    },

    selectRange(date: string, start: string, end: string) {
      if (!this.canEdit) return;
      this.selection = {
        kind: 'range',
        date,
        start,
        end,
        roomId: this.defaultRoomId,
        duration: Number(this.plan.duration) || 30,
      };
      this.builderOpen = false;
      this.roomMenuSlotId = null;
    },

    clearSelection() {
      this.selection = null;
    },

    dropStaleSelection() {
      const { selection } = this;
      if (selection?.kind === 'slot' && !this.selectedSlot) this.selection = null;
      if (selection?.kind === 'break' && !this.selectedBreak) this.selection = null;
    },

    async addRange() {
      const { selection } = this;
      if (selection?.kind !== 'range') return;
      const result = await this.runCommand(['pattern', {
        staffId: this.staffId,
        roomId: Number(selection.roomId || this.defaultRoomId),
        dates: [selection.date],
        start: selection.start,
        end: selection.end,
        duration: Number(selection.duration),
        breaks: [],
      }]);
      if (result.ok && result.slotIds?.length) this.selectSlot(result.slotIds[0]);
    },

    async book(slotId: number) {
      if (this.selectedSlot?.id !== slotId || !this.patientId) return;
      await this.runCommand(['book', slotId, this.patientId]);
    },

    async cancelAppointment(appointmentId: number) {
      await this.runCommand(['cancel', appointmentId]);
    },

    async removeSlot(slotId: number) {
      await this.runCommand(['remove', slotId]);
    },

    async removeBreak(breakId: number) {
      await this.runCommand(['unbreak', breakId]);
    },

    // --- Смена кабинета ---

    toggleRoomMenu(slotId: number) {
      this.roomMenuSlotId = this.roomMenuSlotId === slotId ? null : slotId;
    },

    closeRoomMenu() {
      this.roomMenuSlotId = null;
    },

    async changeRoom(roomId: number) {
      if (this.roomMenuSlotId == null) return;
      await this.runCommand(['room', this.roomMenuSlotId, roomId]);
    },

    // --- Заполнение графика ---

    makePlan(): SchedulePlan {
      return {
        from: this.date,
        to: this.date,
        pattern: 'once',
        anchor: this.date,
        start: '09:00',
        end: '16:00',
        duration: 30,
        roomId: this.defaultRoomId,
        breaks: [],
      };
    },

    ensurePlanRoom() {
      if (!this.allowedRooms.some((room) => room.id === Number(this.plan.roomId))) {
        this.plan.roomId = this.defaultRoomId;
      }
    },

    toggleBuilder() {
      this.builderOpen = !this.builderOpen;
      this.selection = null;
      this.roomMenuSlotId = null;
    },

    addPlanBreak() {
      this.plan.breaks.push({
        label: 'Проветривание', start: '15:00', end: '15:10', scope: 'room',
      });
    },

    removePlanBreak(index: number) {
      this.plan.breaks.splice(index, 1);
    },

    async previewPlan() {
      const fingerprint = JSON.stringify(this.plan);
      const result = await this.preview(['pattern', this.patternSpec]);
      // План успели поменять, пока шёл запрос — результат уже неактуален
      if (result.code === 'CONTEXT_CHANGED' || fingerprint !== JSON.stringify(this.plan)) return;
      if (!result.ok) {
        this.planPreview = { ok: false, text: result.message };
        return;
      }
      const fit = result.unusedMinutes
        ? `Не помещается полный приём: ${result.unusedMinutes} мин. за выбранный период останутся без записи.`
        : 'Все приёмы помещаются между перерывами.';
      this.planPreview = {
        ok: true,
        text: `Новых приёмов: ${result.added || 0}. Новых перерывов: ${result.breaks?.length || 0}. ${fit}`,
      };
    },

    async applyPlan() {
      const spec = this.patternSpec;
      const result = await this.runCommand(['pattern', spec]);
      if (!result.ok) return;
      [this.date] = spec.dates;
      this.selection = null;
      this.builderOpen = false;
      this.planPreview = null;
    },

    // --- Режим филиала ---

    makeBranchDraft(date?: string): BranchRule {
      const effectiveFrom = date || this.date;
      const rule = findBranchRule(this.schedule.branchRules, this.branchId, effectiveFrom);
      const current = clone(rule);
      return {
        week: current?.week || emptyWeek(),
        branchId: Number(this.branchId),
        effectiveFrom,
        exceptions: (current?.exceptions || []).filter((item) => item.date >= effectiveFrom),
      };
    },

    markBranchEdited() {
      this.branchDirty = true;
      this.branchFeedback = null;
    },

    copyMondayToWeekdays() {
      const draft = this.branchDraft;
      const monday = draft?.week.find((day) => day.weekday === 1);
      if (!draft || !monday) return;
      draft.week = draft.week.map((day) => (
        day.weekday > 1 && day.weekday < 6 ? { ...clone(monday), weekday: day.weekday } : day
      ));
      this.markBranchEdited();
    },

    setDayOff(kind: DraftDayKind, index: number, closed: boolean) {
      const day = this.draftDay(kind, index);
      if (!day) return;
      day.open = closed ? [] : [{ start: '08:00', end: '20:00' }];
      day.intake = closed ? [] : [{ start: '09:00', end: '19:00' }];
      this.markBranchEdited();
    },

    // Новый интервал продолжает предыдущий; приём по умолчанию — в часы работы
    addInterval(kind: DraftDayKind, index: number, type: HoursType) {
      const day = this.draftDay(kind, index);
      if (!day) return;
      const previous = day[type][day[type].length - 1];
      const start = previous?.end || (type === 'intake' ? day.open[0]?.start : '08:00') || '09:00';
      const end = type === 'intake' && !previous
        ? day.open[0]?.end || '19:00'
        : toTime(Math.min(1439, toMinutes(start) + 60));
      day[type].push({ start, end });
      this.markBranchEdited();
    },

    removeInterval(kind: DraftDayKind, index: number, type: HoursType, intervalIndex: number) {
      this.draftDay(kind, index)?.[type].splice(intervalIndex, 1);
      this.markBranchEdited();
    },

    addException() {
      const draft = this.branchDraft;
      if (!draft) return;
      if (!isValidDate(draft.effectiveFrom)) {
        this.tell('Укажите дату начала действия режима.', true);
        return;
      }
      // Первая свободная дата начиная с даты вступления режима в силу
      const taken = new Set(draft.exceptions.map((item) => item.date));
      let target = draft.effectiveFrom;
      while (taken.has(target)) target = addDays(target, 1);
      const week = draft.week.find((day) => day.weekday === weekdayOf(target));
      draft.exceptions.push({
        date: target,
        label: '',
        open: clone(week?.open || []),
        intake: clone(week?.intake || []),
      });
      this.markBranchEdited();
    },

    removeException(index: number) {
      this.branchDraft?.exceptions.splice(index, 1);
      this.markBranchEdited();
    },

    async reloadBranch() {
      const effectiveFrom = this.branchDraft?.effectiveFrom;
      if (!isValidDate(effectiveFrom)) {
        this.tell('Укажите корректную дату.', true);
        return;
      }
      if (!(await this.loadSnapshot())) return;
      this.branchDraft = this.makeBranchDraft(effectiveFrom);
      this.branchBaseRevision = this.schedule.revision;
      // Черновик «чистый», только если на эту дату уже есть сохранённое правило
      this.branchDirty = !this.schedule.branchRules.some(
        (rule) => rule.branchId === this.branchId && rule.effectiveFrom === effectiveFrom,
      );
      this.branchFeedback = null;
      this.tell('Сохранённый режим на выбранную дату загружен.');
    },

    async previewBranch() {
      if (!this.branchDraft) return;
      const fingerprint = JSON.stringify(this.branchDraft);
      const spec = { ...clone(this.branchDraft), expectedRevision: this.branchBaseRevision };
      const result = await this.preview(['branch', spec]);
      if (result.code === 'CONTEXT_CHANGED' || fingerprint !== JSON.stringify(this.branchDraft)) {
        return;
      }
      this.branchFeedback = { result, saved: false };
    },

    async saveBranch() {
      if (!this.branchDraft) return;
      const spec = { ...clone(this.branchDraft), expectedRevision: this.branchBaseRevision };
      const result = await this.runCommand(['branch', spec]);
      if (result.code === 'CONTEXT_CHANGED') return;
      if (result.ok) {
        this.branchBaseRevision = this.schedule.revision;
        this.branchDirty = false;
      }
      this.branchFeedback = { result, saved: result.ok };
    },
  },
});
